import React, { useState, useMemo, useCallback } from 'react';
import { 
  Key, 
  Users, 
  Shield, 
  TrendingUp, 
  TrendingDown,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  MapPin,
  Filter,
  Search,
  Download,
  RefreshCw,
} from 'lucide-react';
import { Card } from '../../ui/Card'
import { Badge } from '../../ui/Badge'
import { Button } from '../../ui/Button'
import { useAuthMetrics, useAuthEvents, useSecurityWebSocket } from '../../../hooks/useSecurity'
import { useQueryClient } from '@tanstack/react-query';
import type { AuthResult, AuthMethod } from '../../../types/security'
import { cn } from '../../../utils/cn'
import { formatRelativeTime } from '../../../utils/format'

export const AuthenticationDashboard: React.FC = () => {
  const [selectedTimeRange, setSelectedTimeRange] = useState('24h');
  const [methodFilter, setMethodFilter] = useState<AuthMethod[]>([]);
  const [resultFilter, setResultFilter] = useState<AuthResult[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [realTimeEnabled, setRealTimeEnabled] = useState(true);
  
  const queryClient = useQueryClient();

  const { data: metrics, isLoading: metricsLoading } = useAuthMetrics(
    realTimeEnabled ? 5000 : 30000,
  );
  
  // Create stable time range with ISO string dates to prevent re-renders
  const timeRange = useMemo(() => {
    const range = getTimeRange(selectedTimeRange);
    return {
      start: range.start.toISOString(),
      end: range.end.toISOString(),
    };
  }, [selectedTimeRange]);

  const { data: authEvents, isLoading: _eventsLoading } = useAuthEvents({
    method: methodFilter.length > 0 ? methodFilter : undefined,
    result: resultFilter.length > 0 ? resultFilter : undefined,
    limit: 100,
    timeRange,
  });
  
  // Handle real-time security events
  const handleSecurityEvent = useCallback((event: any) => {
    console.log('[AUTH-DASHBOARD] Received security event:', event);
    
    // Invalidate queries to refresh data when we receive auth events
    if (event?.type === 'auth:event' || event?.data?.type === 'authentication') {
      queryClient.invalidateQueries({ queryKey: ['security', 'auth-events'] });
      queryClient.invalidateQueries({ queryKey: ['security', 'auth-metrics'] });
    }
  }, [queryClient]);
  
  // Subscribe to WebSocket events
  useSecurityWebSocket(handleSecurityEvent);

  const filteredEvents = useMemo(() => {
    console.log('[AUTH-DASHBOARD] authEvents:', authEvents);
    if (!authEvents) {
      return [];
    }
    
    let filtered = authEvents;
    
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter((event: any) =>
        event.userId?.toLowerCase().includes(search) ||
        event.username?.toLowerCase().includes(search) ||
        event.sourceIP?.toLowerCase().includes(search) ||
        event.nodeId?.toLowerCase().includes(search),
      );
    }
    
    return filtered;
  }, [authEvents, searchTerm]);

  const authStats = useMemo(() => {
    if (!authEvents) {
return { byMethod: {}, byResult: {}, byHour: {} };
}
    
    const byMethod = authEvents.reduce((acc: Record<string, number>, event: any) => {
      acc[event.method] = (acc[event.method] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const byResult = authEvents.reduce((acc: Record<string, number>, event: any) => {
      acc[event.result] = (acc[event.result] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const byHour = authEvents.reduce((acc: Record<number, number>, event: any) => {
      const hour = new Date(event.timestamp).getHours();
      acc[hour] = (acc[hour] || 0) + 1;
      return acc;
    }, {} as Record<number, number>);
    
    return { byMethod, byResult, byHour };
  }, [authEvents]);

  const suspiciousEvents = useMemo(() =>
    filteredEvents?.filter((event: any) =>
      event.riskScore > 70 ||
      event.result === 'failure' ||
      event.result === 'blocked',
    ) || [], [filteredEvents],
  );

  const handleExportReport = () => {
    console.log('Exporting authentication report...');
  };

  if (metricsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-600 rounded-lg">
            <Key className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-console-text">Authentication Monitor</h1>
            <p className="text-console-muted">Real-time authentication and access control monitoring</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <Button
            variant={realTimeEnabled ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setRealTimeEnabled(!realTimeEnabled)}
          >
            <RefreshCw className={cn('w-4 h-4 mr-2', realTimeEnabled && 'animate-spin')} />
            Real-time: {realTimeEnabled ? 'ON' : 'OFF'}
          </Button>
          
          <Button variant="secondary" size="sm" onClick={handleExportReport}>
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-console-muted text-sm font-medium">Success Rate</p>
              <p className="text-2xl font-bold text-console-text">
                {metrics?.overview.successRate?.toFixed(1) || '0'}%
              </p>
              <div className="flex items-center gap-1 mt-1">
                <TrendingUp className="w-3 h-3 text-status-online" />
                <span className="text-xs text-status-online">+2.1% vs yesterday</span>
              </div>
            </div>
            <div className="p-3 bg-status-online/20 rounded-lg">
              <CheckCircle2 className="w-6 h-6 text-status-online" />
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-console-muted text-sm font-medium">Active Users</p>
              <p className="text-2xl font-bold text-console-text">
                {metrics?.overview.activeUsers || 0}
              </p>
              <p className="text-xs text-console-muted mt-1">
                {metrics?.overview.uniqueUsers || 0} unique today
              </p>
            </div>
            <div className="p-3 bg-primary-600/20 rounded-lg">
              <Users className="w-6 h-6 text-primary-600" />
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-console-muted text-sm font-medium">MFA Usage</p>
              <p className="text-2xl font-bold text-console-text">
                {metrics?.overview.mfaUsage?.toFixed(1) || '0'}%
              </p>
              <div className="flex items-center gap-1 mt-1">
                <TrendingUp className="w-3 h-3 text-status-online" />
                <span className="text-xs text-status-online">+5.2% vs last week</span>
              </div>
            </div>
            <div className="p-3 bg-secondary-600/20 rounded-lg">
              <Shield className="w-6 h-6 text-secondary-600" />
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-console-muted text-sm font-medium">Failed Attempts</p>
              <p className="text-2xl font-bold text-console-text">
                {metrics?.overview.failedAuths || 0}
              </p>
              <div className="flex items-center gap-1 mt-1">
                <TrendingDown className="w-3 h-3 text-danger-600" />
                <span className="text-xs text-danger-600">-12% vs yesterday</span>
              </div>
            </div>
            <div className="p-3 bg-danger-600/20 rounded-lg">
              <XCircle className="w-6 h-6 text-danger-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Real-time Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-console-text">Authentication Activity</h3>
            <div className="flex items-center gap-2">
              <select
                value={selectedTimeRange}
                onChange={(e) => setSelectedTimeRange(e.target.value)}
                className="bg-console-surface border border-console-border rounded px-3 py-1 text-sm text-console-text"
              >
                <option value="1h">1 hour</option>
                <option value="6h">6 hours</option>
                <option value="24h">24 hours</option>
                <option value="7d">7 days</option>
              </select>
            </div>
          </div>
          
          {/* Chart placeholder - would implement with recharts or similar */}
          <div className="h-64 bg-console-surface/30 rounded-lg border border-console-border flex items-center justify-center">
            <div className="text-center">
              <TrendingUp className="w-8 h-8 text-console-muted mx-auto mb-2" />
              <p className="text-console-muted">Authentication timeline chart</p>
              <p className="text-xs text-console-muted mt-1">
                Success/failure rates over time
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <h3 className="text-lg font-semibold text-console-text mb-4">Methods Used</h3>
          <div className="space-y-3">
            {Object.entries(authStats.byMethod).map(([method, count]) => (
              <div key={method} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={cn(
                    'w-3 h-3 rounded-full',
                    getMethodColor(method),
                  )} />
                  <span className="text-sm text-console-text capitalize">
                    {method.replace('_', ' ')}
                  </span>
                </div>
                <span className="text-sm font-medium text-console-text">{count}</span>
              </div>
            ))}
          </div>
          
          <div className="mt-6 pt-4 border-t border-console-border">
            <div className="text-center">
              <p className="text-xs text-console-muted">Most secure method</p>
              <p className="text-sm font-medium text-console-text mt-1">Certificate + MFA</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card className="p-4">
        <div className="space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-console-muted" />
            <input
              type="text"
              placeholder="Search by user, IP, or node..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-console-surface border border-console-border rounded-lg text-console-text placeholder-console-muted focus:outline-none focus:ring-2 focus:ring-primary-600"
            />
          </div>
          
          {/* Filter Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm text-console-muted">
              <Filter className="w-4 h-4" />
              <span>Filters</span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Method Filter */}
              <div className="space-y-2">
                <label className="text-xs text-console-muted uppercase tracking-wide">Authentication Method</label>
                <div className="bg-gray-800 rounded-lg p-3 space-y-2 max-h-32 overflow-y-auto">
                  {(['password', 'certificate', 'token', 'biometric', 'sso', 'api_key'] as AuthMethod[]).map((method) => (
                    <label key={method} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={methodFilter.includes(method)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setMethodFilter([...methodFilter, method]);
                          } else {
                            setMethodFilter(methodFilter.filter(m => m !== method));
                          }
                        }}
                        className="rounded text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-console-text capitalize">{method.replace('_', ' ')}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Result Filter */}
              <div className="space-y-2">
                <label className="text-xs text-console-muted uppercase tracking-wide">Result Status</label>
                <div className="bg-gray-800 rounded-lg p-3 space-y-2">
                  {(['success', 'failure', 'blocked', 'pending'] as AuthResult[]).map((result) => (
                    <label key={result} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={resultFilter.includes(result)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setResultFilter([...resultFilter, result]);
                          } else {
                            setResultFilter(resultFilter.filter(r => r !== result));
                          }
                        }}
                        className="rounded text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-console-text capitalize">{result}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Events */}
        <div className="mt-6 pt-6 border-t border-console-border space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-console-text">Recent Authentication Events</h4>
            <Badge variant="severity" severity={suspiciousEvents.length > 0 ? 'warning' : 'info'}>
              {suspiciousEvents.length} suspicious
            </Badge>
          </div>
          
          <div className="max-h-96 overflow-y-auto space-y-2">
            {filteredEvents?.slice(0, 20).map((event: any) => (
              <div
                key={event.id}
                className={cn(
                  'p-3 rounded-lg border transition-colors',
                  event.riskScore > 70 || event.result === 'failure' || event.result === 'blocked'
                    ? 'border-danger-600/50 bg-danger-600/10'
                    : 'border-console-border bg-console-surface/30',
                )}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="severity" severity={getResultSeverity(event.result)}>
                      {event.result}
                    </Badge>
                    <Badge variant="default">
                      {event.method}
                    </Badge>
                    {event.mfaUsed && (
                      <Badge variant="severity" severity="info">MFA</Badge>
                    )}
                  </div>
                  <span className="text-xs text-console-muted">
                    {formatRelativeTime(event.timestamp)}
                  </span>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-xs">
                  <div>
                    <span className="text-console-muted">User: </span>
                    <span className="text-console-text font-medium">
                      {event.username || event.userId}
                    </span>
                  </div>
                  
                  <div>
                    <span className="text-console-muted">IP: </span>
                    <span className="text-console-text font-mono">
                      {event.sourceIP}
                    </span>
                  </div>
                  
                  <div>
                    <span className="text-console-muted">Node: </span>
                    <span className="text-console-text">
                      {event.nodeId}
                    </span>
                  </div>
                  
                  <div>
                    <span className="text-console-muted">Risk: </span>
                    <span className={cn(
                      'font-medium',
                      event.riskScore > 70 ? 'text-danger-600' :
                      event.riskScore > 40 ? 'text-warning-600' :
                      'text-status-online',
                    )}>
                      {event.riskScore}
                    </span>
                  </div>
                </div>

                {event.geolocation && (
                  <div className="mt-2 flex items-center gap-1 text-xs text-console-muted">
                    <MapPin className="w-3 h-3" />
                    <span>
                      {event.geolocation.city}, {event.geolocation.country}
                    </span>
                  </div>
                )}

                {event.failureReason && (
                  <div className="mt-2 text-xs text-danger-600">
                    <AlertTriangle className="w-3 h-3 inline mr-1" />
                    {event.failureReason}
                  </div>
                )}
              </div>
            )) || []}
          </div>
          
          {(!filteredEvents || filteredEvents.length === 0) && (
            <div className="text-center py-8">
              <Key className="w-8 h-8 text-console-muted mx-auto mb-2" />
              <p className="text-console-muted">No authentication events found</p>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

// Helper functions
function getTimeRange(range: string): { start: Date; end: Date } {
  const end = new Date();
  const start = new Date();
  
  switch (range) {
    case '1h':
      start.setHours(start.getHours() - 1);
      break;
    case '6h':
      start.setHours(start.getHours() - 6);
      break;
    case '24h':
      start.setHours(start.getHours() - 24);
      break;
    case '7d':
      start.setDate(start.getDate() - 7);
      break;
    default:
      start.setHours(start.getHours() - 24);
  }
  
  return { start, end };
}

function getResultSeverity(result: AuthResult): 'info' | 'warning' | 'error' | 'critical' {
  switch (result) {
    case 'success': return 'info';
    case 'failure': return 'error';
    case 'blocked': return 'critical';
    case 'pending': return 'info';
    default: return 'info';
  }
}

function getMethodColor(method: string): string {
  const colors = {
    password: 'bg-primary-600',
    certificate: 'bg-status-online',
    token: 'bg-secondary-600',
    biometric: 'bg-warning-600',
    sso: 'bg-purple-600',
    api_key: 'bg-orange-600',
  };
  return colors[method as keyof typeof colors] || 'bg-console-muted';
}